import { injectNotes } from 'lowcomp/plugins/member/utils';
import React, { createContext, Dispatch, useCallback, useContext, useReducer } from 'react';
import { message } from '@/components/UI';
import { checkBlacklist, getRewardRedeemCoupon, getRewardUserInfo, queryFreeProductList, queryIssuedThirdCouponList, queryThirdCouponList } from '@/service';
import { FormatMoney } from '@/utils';
import { getIsLogin } from '@/utils/cookie';

export type IUserLevelInfo = {
    level: number;
    name: string;
    normalName: string;
    plusName: string;
    minPoint: number;
    validStartTime: string;
    validEndTime: string;
};
export interface EcoState {
    // 是否在黑名单中
    inBlackList: boolean;
    // 用户积分详情
    rewardUserInfo: {
        accumPoints: string;
        frozenPoints: string;
        points: number;
        sign: string;
        userId: string;
        version: number;
        expiringPoints: string;
    } | null;
    rewardUserLevel: IUserLevelInfo | null;

    // 已兑换券
    COUPONS_REDEEMED: any[];
    FREE_PRODUCTS_REDEEMED: any[];
    THIRD_COUPONS_REDEEMED: any[];

    // Issued 第三方
    ISSEUED_THIRD_COUPONS_REDEEMED: any[];
}

export interface EcoAction {
    type: string;
    payload: any;
}

const initState: EcoState = {
    inBlackList: false,
    // 用户积分详情
    rewardUserInfo: null,
    rewardUserLevel: null,
    // 已兑换券
    COUPONS_REDEEMED: [],
    FREE_PRODUCTS_REDEEMED: [],
    THIRD_COUPONS_REDEEMED: [],

    // Issued 第三方
    ISSEUED_THIRD_COUPONS_REDEEMED: [],
};

const ecoReducer = (state: EcoState, action: EcoAction): EcoState => {
    const { type, payload } = action;

    switch (type) {
        case 'changeBlackList':
            return { ...state, inBlackList: payload };
        case 'CHANGR_REWARD_USERINFO':
            return { ...state, rewardUserInfo: payload };
        case 'changeRewardUserLevel':
            return { ...state, rewardUserLevel: payload };
        case 'GET_COUPONS_REDEEMED':
            return { ...state, COUPONS_REDEEMED: payload };
        case 'GET_FREE_PRODUCTS_REDEEMED':
            return { ...state, FREE_PRODUCTS_REDEEMED: payload };

        case 'GET_THIRD_COUPONS_REDEEMED':
            return { ...state, THIRD_COUPONS_REDEEMED: payload };
        case 'GET_ISSUED_THIRD_COUPONS_REDEEMED':
            return { ...state, ISSEUED_THIRD_COUPONS_REDEEMED: payload };
        default:
            return state;
    }
};

export const EcoContext = createContext<{ state: EcoState; dispatch: React.Dispatch<EcoAction> }>({
    state: initState,
    dispatch: () => null,
});

const handleCouponsRedeemed = async ({ locale, t, timezoneList }: any) => {
    let rewardRedeemRules = [];
    try {
        const res = await getRewardRedeemCoupon(locale);
        if (res?.code === '0') {
            const flatRules = res.data?.map((item: any) => {
                let minimum_rule = '';
                if (item?.prerequisite === 'PRICES') {
                    // 最低金额
                    const price = parseInt(item.prerequisiteRange);
                    const currencySymbol = item?.ruleWebsite?.currencySymbol || '';
                    const rangeConfig = ['uk', 'br', 'mx'].includes(locale)
                        ? { price: currencySymbol + FormatMoney(price) }
                        : { price: FormatMoney(price) + currencySymbol };
                    const prerequisiteRange = t('minimum_price', rangeConfig);
                    minimum_rule = prerequisiteRange;
                }
                if (item?.prerequisite === 'QUANTITY') {
                    // 最低数量
                    const num = parseInt(item.prerequisiteRange);
                    const prerequisiteRange = t('minimum_num', { num });
                    minimum_rule = prerequisiteRange;
                }

                return {
                    ...item,
                    minimum_rule,
                };
            });

            rewardRedeemRules = injectNotes(flatRules || [], t, locale, timezoneList);
            console.log('--->>>>', rewardRedeemRules);
        }
    } catch (err) {
        console.log(err);
    }
    return rewardRedeemRules;
};

const asyncDispatch = (dispatch: Dispatch<EcoAction>) =>
    useCallback(async ({ type, payload }: EcoAction) => {
        switch (type) {
            case 'checkBlackList':
                try {
                    const res = await checkBlacklist(payload.locale);
                    // res.data.inBlackList = true;
                    dispatch({
                        type: 'changeBlackList',
                        payload: res.data.inBlackList,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'changeRewardUserInfo':
                try {
                    if (!getIsLogin()) return;
                    const res = await getRewardUserInfo(payload.locale);
                    if (res?.code !== '0') return message.error(res.msg);
                    dispatch({
                        type: 'CHANGR_REWARD_USERINFO',
                        payload: res.data || {},
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'getCouponsRedeemed': // 查询用户已兑换优惠券 -- 常规
                const couponsRedeemedList = await handleCouponsRedeemed(payload);

                dispatch({
                    type: 'GET_COUPONS_REDEEMED',
                    payload: couponsRedeemedList,
                });
                return;
            case 'getProducts': // 查询用户已兑换优惠券 -- 实体产品
                queryFreeProductList(payload.locale).then((res) => {
                    if (res?.code === '0') {
                        const FreeProducts = res?.data?.map((coupon: any) => {
                            const noteList = coupon?.ruleDescription?.split('\n')?.filter((str: string) => !!str);
                            return {
                                ...coupon,
                                noteList,
                            };
                        });
                        dispatch({
                            type: 'GET_FREE_PRODUCTS_REDEEMED',
                            payload: FreeProducts || [],
                        });
                    }
                });
                return;
            case 'getThiredCoupons': // 查询用户已兑换优惠券 -- 第三方优惠券
                queryThirdCouponList(payload.locale).then((res) => {
                    if (res?.code === '0') {
                        const thirdCoupons = res?.data?.map((coupon: any) => {
                            const noteList = coupon?.ruleDescription?.split('\n')?.filter((str: string) => !!str);
                            return {
                                ...coupon,
                                noteList,
                            };
                        });
                        dispatch({
                            type: 'GET_THIRD_COUPONS_REDEEMED',
                            payload: thirdCoupons || [],
                        });
                    }
                });
                return;
            case 'getIssuedThiredCoupons': // 查询用户已兑换优惠券 -- 第三方优惠券
                queryIssuedThirdCouponList(payload.locale).then((res) => {
                    if (res?.code === '0') {
                        const IssuedThirdCoupons = res?.data?.map((coupon: any) => {
                            const noteList = coupon?.ruleDescription?.split('\n')?.filter((str: string) => !!str);
                            return {
                                ...coupon,
                                noteList,
                            };
                        });
                        dispatch({
                            type: 'GET_ISSUED_THIRD_COUPONS_REDEEMED',
                            payload: IssuedThirdCoupons || [],
                        });
                    }
                });
                return;
            default:
                dispatch({ type, payload });
        }
    }, []);

export function EcoProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(ecoReducer, initState);
    return <EcoContext.Provider value={{ state, dispatch: asyncDispatch(dispatch) }}>{children}</EcoContext.Provider>;
}

export const useEcoStore = () => {
    const store = useContext(EcoContext);
    const state = store.state;
    return state;
};

export const useEcoDispatch = () => {
    const store = useContext(EcoContext);
    const dispatch = store.dispatch;
    return dispatch;
};
